import React, { useEffect, useRef, useState } from "react";
import { api, apiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Download, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// mode: "self" (patient) | "fiche" (gerant). For "fiche", pass patientId.
export function DocumentManager({ mode, patientId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const listUrl = mode === "self" ? "/patient/documents" : `/patients/${patientId}/documents`;

  const load = () => api.get(listUrl).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, mode]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(listUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document téléversé (stockage privé chiffré)");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const open = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}/url`);
      window.open(`${API_BASE}/documents/download?token=${data.token}`, "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (doc) => {
    const url = mode === "self" ? `/patient/documents/${doc.id}` : `/patients/${patientId}/documents/${doc.id}`;
    await api.delete(url);
    load();
  };

  return (
    <div className="space-y-3" data-testid="document-manager">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> Stockage privé · liens temporaires 5 min
        </p>
        <Button size="sm" className="gap-2" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Envoi…" : "Téléverser"}
        </Button>
        <input ref={fileRef} type="file" className="hidden" onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" data-testid="doc-file-input" />
      </div>
      <div className="space-y-2" data-testid="doc-list">
        {docs.length === 0 && <p className="text-sm text-muted-foreground">Aucun document.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2" data-testid={`doc-${d.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm truncate">{d.filename}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => open(d)} data-testid={`doc-open-${d.id}`}><Download className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
